// Website-published records and copy. Sources:
// https://www.oceansideliving.com.au/designs-10m
// https://www.oceansideliving.com.au/designs-15m
// Check every figure against owner-approved plan revisions before launch.

export type Frontage = "10m" | "15m";

export type Design = {
  slug: string;
  name: string;
  frontage: Frontage;
  storeys: string;
  bedrooms: number;
  bathrooms: number;
  carSpaces: number;
  livingArea: number;
  totalUnderRoof: number;
  outdoorNote: string;
  tagline: string;
  description: string;
  features: string[];
  lifestyleFit: string;
  image: string;
  sourceUrl: string;
};

export const designs: Design[] = [
  {
    slug: "the-solene",
    name: "The Solene",
    frontage: "10m",
    storeys: "Single-storey",
    bedrooms: 3,
    bathrooms: 2,
    carSpaces: 2,
    livingArea: 131,
    totalUnderRoof: 176.8,
    outdoorNote: "Includes an 8.6m² alfresco",
    tagline: "Open-plan living to the alfresco, plus a separate theatre.",
    description:
      "The Solene packs a lot into a 10m frontage without feeling tight. An open-plan kitchen, dining and living area opens to the alfresco, with a separate theatre for movie nights or a quiet retreat. The master suite has a walk-in robe and ensuite, while two minor bedrooms share the main bathroom. A walk-in linen and separate laundry add the storage a narrow home often lacks.",
    features: ["Separate theatre room", "Master suite with walk-in robe and ensuite", "Walk-in linen cupboard", "Separate laundry"],
    lifestyleFit: "Couples, small households and investors",
    image: "https://cdn.magicpatterns.com/uploads/6Y8gY32WSrsyTFzJCAhZg3/THE_SOLENE_Render-_10m_(3_bed).avif",
    sourceUrl: "https://www.oceansideliving.com.au/designs-10m",
  },
  {
    slug: "the-cortez",
    name: "The Cortez",
    frontage: "10m",
    storeys: "Single-storey",
    bedrooms: 4,
    bathrooms: 2,
    carSpaces: 2,
    livingArea: 136,
    totalUnderRoof: 179.5,
    outdoorNote: "Includes a 9.4m² alfresco",
    tagline: "Four bedrooms on a 10m block, rare at this width.",
    description:
      "The Cortez fits four bedrooms onto a 10m block, which is rare at this width. The kitchen includes a walk-in pantry and flows to the dining, living and alfresco. A separate theatre gives the household a second living zone, and a separate WC takes the morning pressure off the main bathroom.",
    features: ["Master suite with walk-in robe and ensuite", "Walk-in pantry", "Separate theatre", "Separate WC"],
    lifestyleFit: "Households that want a fourth bedroom, guest room or home office without a wider block",
    image: "https://cdn.magicpatterns.com/uploads/khykCSRdaBpHccCMVQ54Qe/THE_CORTEZ_Render-_10m_(4_bed).avif",
    sourceUrl: "https://www.oceansideliving.com.au/designs-10m",
  },
  {
    slug: "the-azure",
    name: "The Azure",
    frontage: "15m",
    storeys: "Single-storey",
    bedrooms: 3,
    bathrooms: 2,
    carSpaces: 2,
    livingArea: 182,
    totalUnderRoof: 243.8,
    outdoorNote: "Includes a 7.6m² front verandah and 16.7m² alfresco",
    tagline: "Generous rooms and a welcoming front verandah.",
    description:
      "The Azure is a spacious three-bedroom home with a welcoming front verandah that gives it real street appeal. Inside, the kitchen has a walk-in pantry and opens to a large dining and living area, then out to a 16.7m² alfresco. A separate theatre provides a second living space. The master suite has a walk-in robe and ensuite with separate WC, and the main bathroom includes a full-size bath and its own WC.",
    features: ["Front verandah", "Walk-in pantry and dedicated laundry", "Separate theatre", "Ensuite with separate WC"],
    lifestyleFit: "Downsizers and couples who want every room to feel generous",
    image: "https://cdn.magicpatterns.com/uploads/iNbgxVduyyVCZ7hgYjjXKq/THE_AZURE_Render-_15m_(3_bed).avif",
    sourceUrl: "https://www.oceansideliving.com.au/designs-15m",
  },
  {
    slug: "the-hacienda",
    name: "The Hacienda",
    frontage: "15m",
    storeys: "Single-storey",
    bedrooms: 4,
    bathrooms: 2,
    carSpaces: 2,
    livingArea: 186,
    totalUnderRoof: 246.2,
    outdoorNote: "Includes a 21m² alfresco, the largest in our range",
    tagline: "Our largest single-storey design, built for entertaining.",
    description:
      "The Hacienda is our largest single-storey design and built for entertaining. A 21m² alfresco, the biggest in our range, extends the open-plan kitchen, dining and living outdoors. The kitchen includes a walk-in pantry, and a separate theatre gives the household a second retreat. Four bedrooms, a walk-in linen and a dedicated laundry make it an easy home to run with a full house.",
    features: ["21m² alfresco", "Ensuite with separate WC", "Walk-in pantry and walk-in linen", "Main bathroom with full bath and separate WC"],
    lifestyleFit: "People who entertain often and households that need a guest room or home office",
    image: "https://cdn.magicpatterns.com/uploads/61H1rR7SqvvGFjDZXWqzHm/THE_HACIENDA_Render-_15m_(4_bed).avif",
    sourceUrl: "https://www.oceansideliving.com.au/designs-15m",
  },
];

export const frontageGuides: { frontage: Frontage; title: string; intro: string; points: { title: string; text: string }[] }[] = [
  {
    frontage: "10m",
    title: "Building on a 10m block",
    intro: "A 10m frontage is common in newer estates and infill lots, and it rewards careful planning.",
    points: [
      { title: "Light down the length of the home", text: "We position living areas and use windows on the open side of the block to bring daylight deep into the plan." },
      { title: "Garage dominance", text: "A double garage takes up much of a 10m frontage. We balance it with a defined entry and porch for street presence." },
      { title: "Setbacks and boundary walls", text: "Where applicable, we use a boundary wall to free up width inside the home." },
      { title: "Corridor efficiency", text: "Our plans keep circulation tight so the rooms get the space." },
    ],
  },
  {
    frontage: "15m",
    title: "Building on a 15m block",
    intro: "A 15m frontage gives you the width to spread out, and opens up layouts narrower lots can't offer.",
    points: [
      { title: "Zoned living", text: "The extra width lets us separate the master suite from the minor bedrooms." },
      { title: "Bigger rooms, not just more rooms", text: "Bedrooms, living areas and the kitchen all gain width, so the home feels generous throughout." },
      { title: "A true outdoor room", text: "Wider blocks allow alfresco areas of 16 to 21m², big enough for dining and lounge settings together." },
      { title: "Street presence", text: "More frontage beside the garage allows a feature entry or front verandah, as on The Azure." },
    ],
  },
];
